import { useEffect, useMemo, useState, useCallback } from "react";
import { getRoles, createRole, updateRole } from "../../../services/admin/roles_admin";
import RolFormModal from "./RolesFormModal";
import AsignarPermisosModal from "./AsignarPermisosModal";
import { IconEdit, IconConfig } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";

const PAGE_SIZE = 12;

function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const [showPermisosModal, setShowPermisosModal] = useState(false);
  const [roleForPermisos, setRoleForPermisos] = useState(null);

  // ✅ Loading + Updating
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  // ✅ Alert simple
  const [alert, setAlert] = useState(null); // { type:"success"|"error"|"info", message:string }

  const getNiceErrorMessage = (error, fallback = "Something went wrong.") => {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      fallback;

    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 220 ? clean.slice(0, 220) + "…" : clean;
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    window.clearTimeout(showAlert._t);
    showAlert._t = window.setTimeout(() => setAlert(null), 3500);
  };

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const array = await getRoles();
      setRoles(Array.isArray(array) ? array : []);
    } catch (err) {
      console.error("Error loading roles:", err);
      showAlert("error", getNiceErrorMessage(err, "Error loading roles."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  // ✅ filtro + totalPages derivado
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return roles;
    return roles.filter((r) => String(r?.nombre || "").toLowerCase().includes(term));
  }, [roles, search]);

  const totalPages = useMemo(() => {
    const tp = Math.ceil(filtered.length / PAGE_SIZE);
    return tp > 0 ? tp : 1;
  }, [filtered.length]);

  // ✅ corrige page si se sale del rango por filtro
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const currentData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const nextPage = () => page < totalPages && setPage(page + 1);
  const prevPage = () => page > 1 && setPage(page - 1);

  const openCreateModal = () => {
    setSelectedRole(null);
    setShowModal(true);
  };

  const openEditModal = (rol) => {
    setSelectedRole(rol);
    setShowModal(true);
  };

  const openPermisosModal = (rol) => {
    setRoleForPermisos(rol);
    setShowPermisosModal(true);
  };

  const handleCreate = async (formData) => {
    setUpdating(true);
    try {
      await createRole(formData);
      await loadRoles();
      setShowModal(false);
      showAlert("success", "Role created successfully.");
    } catch (err) {
      console.error("Error creating role:", err);
      showAlert("error", getNiceErrorMessage(err, "Error creating role."));
      throw err; // ✅ para que el modal pueda mostrar su propio alert si lo tenés así
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdate = async (id, formData) => {
    setUpdating(true);
    try {
      await updateRole(id, formData);
      await loadRoles();
      setShowModal(false);
      showAlert("success", "Role updated successfully.");
    } catch (err) {
      console.error("Error updating role:", err);
      showAlert("error", getNiceErrorMessage(err, "Error updating role."));
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="page-container">
      <style>{modernStyles}</style>

      {/* ✅ ALERT */}
      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button className="omx-alert__x" onClick={() => setAlert(null)}>✕</button>
        </div>
      )}

      <h2 className="title">Roles Management</h2>

      <div className="top-bar">
        <input
          type="text"
          placeholder="Search role..."
          className="search-input"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        {hasPerm("Create rol") && (
          <button
            className="btn-create"
            onClick={openCreateModal}
            disabled={loading || updating}
            title={loading ? "Loading..." : updating ? "Updating..." : ""}
            style={{ opacity: loading || updating ? 0.7 : 1 }}
          >
            {updating ? "Please wait..." : "Create Role"}
          </button>
        )}
      </div>

      <div className="table-card">
        <table className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Role Name</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="3" className="no-data">Loading roles...</td>
              </tr>
            ) : currentData.length === 0 ? (
              <tr>
                <td colSpan="3" className="no-data">No roles found</td>
              </tr>
            ) : (
              currentData.map((r) => (
                <tr key={r.rol_id}>
                  <td>{r.rol_id}</td>
                  <td>{r.nombre}</td>

                  <td>
                    <div className="actions">
                      {hasPerm("Update rol") && (
                        <span
                          className="action-icon edit"
                          onClick={() => !updating && openEditModal(r)}
                          style={{ opacity: updating ? 0.6 : 1, pointerEvents: updating ? "none" : "auto" }}
                          title="Edit"
                        >
                          {IconEdit}
                        </span>
                      )}

                      {hasPerm("Assign Permissions rol") && (
                        <span
                          className="action-icon config"
                          onClick={() => !updating && openPermisosModal(r)}
                          style={{ opacity: updating ? 0.6 : 1, pointerEvents: updating ? "none" : "auto" }}
                          title="Assign permissions"
                        >
                          {IconConfig}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="pagination">
        <button onClick={prevPage} disabled={page === 1 || loading}>
          Prev
        </button>

        <span>
          Page {page} of {totalPages}
        </span>

        <button onClick={nextPage} disabled={page === totalPages || loading}>
          Next
        </button>
      </div>

      {/* MODAL - CREATE/EDIT */}
      {showModal && (
        <RolFormModal
          isOpen={showModal}
          onClose={() => !updating && setShowModal(false)}
          rolData={selectedRole}
          onSubmit={(formData) => {
            if (selectedRole) return handleUpdate(selectedRole.rol_id, formData);
            return handleCreate(formData);
          }}
        />
      )}

      {/* MODAL - PERMISSIONS */}
      {showPermisosModal && roleForPermisos && (
        <AsignarPermisosModal
          rol={roleForPermisos}
          onClose={() => setShowPermisosModal(false)}
        />
      )}
    </div>
  );
}

/* ------------------ STYLES ------------------ */
const modernStyles = `
.page-container {
  /* Llena el alto de la tarjeta del layout; solo el listado scrollea. */
  height: 100%;
  padding: 4px 0 0;
  font-family: 'Segoe UI';
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
/* Cabecera, alerta y paginacion conservan su alto; no se comprimen. */
.page-container > .header,
.page-container > .pagination,
.page-container > .omx-alert,
.page-container > .lista-recortada {
  flex: none;
}


/* ✅ ALERT */
.omx-alert{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:12px 14px;
  border-radius:12px;
  border:1px solid;
  margin: 0 0 14px 0;
  box-shadow: 0 10px 25px rgba(0,0,0,.08);
}
.omx-alert__msg{ font-size:14px; font-weight:700; }
.omx-alert__x{
  border:none;
  background:transparent;
  cursor:pointer;
  font-size:18px;
  opacity:.7;
}
.omx-alert__x:hover{ opacity:1; }

.omx-alert--success{ background:#ECFDF5; border-color:#A7F3D0; color:#065F46; }
.omx-alert--error{ background:#FEF2F2; border-color:#FECACA; color:#7F1D1D; }
.omx-alert--info{ background:#EFF6FF; border-color:#BFDBFE; color:#1E3A8A; }

.title {
  font-size: 26px;
  font-weight: 700;
  color: #2F4779;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  gap: 12px;
}

.search-input {
  padding: 10px 16px;
  width: 260px;
  border-radius: 8px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  color: #1E293B;
  font-size: 14px;
}

.btn-create {
  padding: 10px 18px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
}

.btn-create:hover {
  background: #2563EB;
}

.table-card {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  margin-top: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);

  /* min-height: 0 permite encogerse por debajo del contenido; sin eso
     el scroll interno no ocurre. */
  flex: 0 1 auto;
  min-height: 0;
  overflow: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table th {
  background: #F1F5F9;
  padding: 14px;
  font-size: 13px;
  color: #475569;
  font-weight: 700;
  text-align: left;
  border-bottom: 1px solid #E2E8F0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.modern-table td {
  padding: 14px;
  font-size: 14px;
  color: #334155;
  border-bottom: 1px solid #F1F5F9;
}

.modern-table tr:hover td {
  background: #F8FAFC;
}

.actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.action-icon {
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.2s;
  display: inline-flex;
}

.action-icon.edit:hover {
  background: #DBEAFE;
}

.action-icon.config:hover {
  background: #FDE68A;
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #94A3B8;
}

.pagination {
  margin-top: 14px;
  margin-bottom: 2px;
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
}

.pagination button {
  padding: 8px 14px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.pagination button:disabled {
  opacity: .5;
}
.pagination span {
  font-size: 14px;
  color: #475569;
}
`;

export default RolesPage;
